import type { Lead, TruckRequest } from '@otw/models/leads/lead'
import type { NotificationPreference } from '@otw/models'
import { VEHICLE_SIZE_FILTER_OPTIONS } from 'constants/inventory'

const VALID_SIZE_KEYS = new Set(VEHICLE_SIZE_FILTER_OPTIONS.map(opt => opt.value))

const isConcreteValue = (value: string): boolean => {
  const trimmed = value.trim()
  return trimmed.length > 0 && trimmed.toLowerCase() !== 'any'
}

/**
 * Builds valid Vehicle Added notification size keys (`"Type|Size"`) from a lead's
 * truck request. Skips blank/"Any" values and invalid type+size pairs.
 */
export const getLeadVehicleAddedSizeKeys = (
  truckRequest: TruckRequest | Lead['truckRequest'] | undefined | null,
): string[] => {
  const types = truckRequest?.types
  if (!types?.length) return []

  const keys = new Set<string>()
  for (const entry of types) {
    const typeList = Array.isArray(entry.type) ? entry.type : entry.type ? [entry.type] : []
    const sizeList = Array.isArray(entry.size) ? entry.size : entry.size ? [entry.size] : []

    for (const type of typeList) {
      if (!isConcreteValue(type)) continue
      for (const size of sizeList) {
        if (!isConcreteValue(size)) continue
        const key = `${type.trim()}|${size.trim()}`
        if (VALID_SIZE_KEYS.has(key)) {
          keys.add(key)
        }
      }
    }
  }

  return [...keys]
}

/** Human-readable label for a `"Type|Size"` key, e.g. `"Box truck - 12ft"`. */
export const formatVehicleAddedSizeKeyLabel = (key: string): string => {
  const option = VEHICLE_SIZE_FILTER_OPTIONS.find(opt => opt.value === key)
  if (option) return option.label
  const [type, size] = key.split('|')
  return type && size ? `${type} - ${size}` : key
}

/**
 * Returns lead size keys that are not covered by the user's Vehicle Added prefs.
 * Covered = at least one channel on AND (empty sizes = all, or key is in sizes).
 */
export const getUncoveredVehicleAddedSizeKeys = (
  leadSizeKeys: string[],
  preference: NotificationPreference | undefined | null,
): string[] => {
  if (!leadSizeKeys.length) return []

  const channelOn = !!(preference?.inApp || preference?.email || preference?.sms)
  const selectedSizes = preference?.sizes ?? []

  if (channelOn && selectedSizes.length === 0) {
    // Empty sizes with a channel on means all sizes are covered
    return []
  }

  if (!channelOn) {
    return leadSizeKeys
  }

  return leadSizeKeys.filter(key => !selectedSizes.includes(key))
}
